import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, PartyPopper, Zap, ShoppingCart, CheckCircle2, AlertTriangle, Gift, Wrench, Trash2, MessageSquare, Settings2, Receipt, ChevronDown, ChevronUp } from "lucide-react";
import { isFromAdminFestivals, clearAdminFestivalEntry } from "@/lib/adminNav";
import EarlyAccessBanner from "@/pages/components/organiser/EarlyAccessBanner";
import { EventWindowCard } from "@/components/EventWindowCard";
import { OutstandingExtensions } from "@/components/OutstandingExtensions";
import { PostFestivalEmailsCard } from "@/components/PostFestivalEmailsCard";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const DEVICE_LIMIT_ROLES = [
  ["check_in_helper", "Check-In Helper"],
  ["court_ops_helper", "Court Ops"],
  ["roster_ops_helper", "OPS Assist"],
  ["deputy_helper", "Deputy Admin"],
];

const STATE_LABELS = {
  draft: { label: "Draft", tone: "border-[#525252] text-[#A3A3A3]" },
  demo_active: { label: "Demo Active", tone: "border-[#CCFF00] text-[#CCFF00]" },
  demo_expired: { label: "Demo Expired", tone: "border-[#FFB020] text-[#FFB020]" },
  payment_pending: { label: "Payment Pending", tone: "border-[#FFB020] text-[#FFB020]" },
  active_paid: { label: "Active", tone: "border-[#CCFF00] text-[#CCFF00]" },
  override: { label: "Override", tone: "border-[#66C2FF] text-[#66C2FF]" },
  complimentary: { label: "Complimentary", tone: "border-[#66C2FF] text-[#66C2FF]" },
  expired: { label: "Expired", tone: "border-[#525252] text-[#525252]" },
  draft_expired: { label: "Draft Expired", tone: "border-[#FF4500] text-[#FF4500]" },
};

function formatSeconds(total) {
  if (total == null) return "";
  const m = Math.floor(total / 60).toString().padStart(2, "0");
  const s = Math.floor(total % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function FestivalDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [festival, setFestival] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(null);
  // Iter316 — Delete-draft confirmation modal state.
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  // Iter325 — Bespoke package overrides (if the platform owner has
  // configured one for this festival). Used to render side-by-side
  // standard-vs-bespoke pricing on the add-ons modal.
  const [bespoke, setBespoke] = useState(null);
  // Iter325 — Custom package request CTA. Small textarea + submit
  // wired to POST /festival/enquiries.
  const [customPkgOpen, setCustomPkgOpen] = useState(false);
  const [customPkgText, setCustomPkgText] = useState("");
  const [customPkgBusy, setCustomPkgBusy] = useState(false);
  // Iter316 B1 — Add-ons step. Instead of jumping straight to Stripe when
  // a tier tile is clicked, open a modal so the organiser can bolt on
  // Self-Service + helper bundles. Continue posts to /festival-basket/checkout.
  const [addonsFor, setAddonsFor] = useState(null); // pass_type key of clicked tile
  const [addonsQty, setAddonsQty] = useState({});   // { bolt_on_key → qty }
  const [addonsSelfServe, setAddonsSelfServe] = useState(false);
  // Iter327 · Bug 5 — Pre-purchase cap enforcement. Fetch the current
  // roster count from the cap-status endpoint so we can grey out
  // tiers whose player_cap is already exceeded before checkout.
  const [capCount, setCapCount] = useState(null);
  // Iter371 · Fix 4 — Advanced section expand/collapse + Purchase
  // History log for Organiser + Deputy. Fetched on first expand.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [purchases, setPurchases] = useState(null);
  const [purchasesBusy, setPurchasesBusy] = useState(false);

  const isPlatformOwner = user?.role === "admin" || (user?.permissions || []).some((p) => p === "admin" || p === "super_admin");

  const load = async () => {
    try {
      const [f, p, bp] = await Promise.all([
        api.get(`/festival/${id}`),
        api.get(`/festival/pricing`),
        // Iter325 — Bespoke package (organiser + owner can read).
        // Falls back to null if none exists; render side-by-side
        // pricing on the add-ons modal only when present.
        api.get(`/festival/${id}/bespoke`).catch(() => ({ data: { bespoke: null } })),
      ]);
      setFestival(f.data);
      setPricing(p.data.pricing);
      setBespoke(bp.data?.bespoke || null);
      setRemaining(f.data.license?.remaining_seconds ?? null);
      // Iter327 · Bug 5 — Pull current roster count so the tier grid
      // can grey out passes whose cap is already breached.
      try {
        const cs = await api.get(`/festival/${id}/players/cap-status`);
        if (cs?.data && typeof cs.data.count === "number") setCapCount(cs.data.count);
      } catch { /* endpoint may 402 pre-license; treat as unknown */ }
    } catch (e) {
      toast.error("Failed to load festival");
      navigate("/festival/mine");
    }
  };

  useEffect(() => { load(); }, [id]);
  // Iter327 · Bug 6 — Auto-open the add-ons modal when we land here
  // with a `#addons` hash (from the "Add Self-Service" hint on the
  // Player Manager cap-locked banner). Picks the currently-active
  // paid tier if any, else the smallest tier that fits the roster.
  useEffect(() => {
    if (!pricing || !festival) return;
    if (window.location.hash !== "#addons") return;
    const currentTier = festival?.license?.pass_type;
    let target = currentTier && pricing[currentTier] ? currentTier : null;
    if (!target) {
      const passes = Object.entries(pricing)
        .filter(([k, v]) => v?.tier !== "bolton" && !v?.poa && (Number(v?.amount) > 0))
        .sort((a, b) => (Number(a[1].amount) || 0) - (Number(b[1].amount) || 0));
      if (passes.length) target = passes[0][0];
    }
    if (target) {
      startCheckout(target);
      // Clear the hash so a refresh doesn't re-open the modal.
      try { window.history.replaceState(null, "", window.location.pathname); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricing, festival?.id]);

  // Local countdown tick to avoid a request every second. Server owns truth.
  useEffect(() => {
    if (!festival?.license || festival.license.state !== "demo_active") return;
    if (remaining == null) return;
    const t = setInterval(() => setRemaining((r) => (r != null && r > 0 ? r - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [festival?.license?.state, festival?.license?.demo_ends_at]);

  // Refresh from server every 15s while active so we catch server-side transitions.
  useEffect(() => {
    if (!festival) return;
    const t = setInterval(() => load(), 15000);
    return () => clearInterval(t);
  }, [festival?.id]);

  const startDemo = async () => {
    setBusy(true);
    try {
      await api.post(`/festival/${id}/demo/start`);
      toast.success("Demo started — 5 minutes on the clock");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Demo start failed");
    } finally { setBusy(false); }
  };

  const startCheckout = async (passType) => {
    // Iter316 B1 — Open the add-ons modal instead of redirecting
    // straight to Stripe. Organiser picks Self-Service + helper
    // bundles + qty here and Continue submits the basket.
    setAddonsFor(passType);
    setAddonsQty({});
    // Default Self-Service to the tier's included state — if the
    // pricing row says it's included, pre-tick; otherwise start off.
    const def = pricing?.[passType] || {};
    setAddonsSelfServe(!!def.self_service_included);
  };

  // Iter316 B1 — Basket bolt-on keys the modal exposes. Order matters
  // for the UI. Self-Service is picked separately so we can show its
  // toggle instead of a qty spinner.
  const HELPER_BOLTONS = [
    { key: "bolton_helper_seat", label: "Extra Check-In helper", hint: "One shift pass for a Check-In helper (roster + tap check-in)." },
    { key: "bolton_ops_seat", label: "Extra Court OPS helper", hint: "One shift pass for a Court OPS helper (drag/drop + timers)." },
    { key: "bolton_deputy_seat", label: "Extra Deputy", hint: "One shift pass for a Deputy (OPS + Player Manager)." },
  ];

  const selfServeBoltonKey = () => {
    if (!pricing) return null;
    // Iter466 — Tiers with no included self-service get a per-day-count
    // matching bolt-on so a 2-day Standard pays more than a 1-day
    // Standard. The passType looks like "starter_2day" / "standard_1day"
    // — split and compose the full bolt-on key.
    const passType = addonsFor || "";
    const [tier, dayPart] = passType.split("_");
    if (!tier || !dayPart) return null;
    if (tier === "starter" || tier === "standard") {
      const candidate = `bolton_self_service_${tier}_${dayPart}`;
      if (pricing[candidate]) return candidate;
    }
    return null; // Pro/Unlimited include self-service, so no bolt-on needed.
  };

  const addonsSelfIncluded = pricing?.[addonsFor]?.self_service_included;
  const addonsSelfBoltKey = selfServeBoltonKey();

  const addonsTotal = (() => {
    if (!pricing || !addonsFor) return 0;
    const base = Number(pricing[addonsFor]?.amount) || 0;
    let extras = 0;
    for (const [k, qty] of Object.entries(addonsQty)) {
      const q = Number(qty) || 0;
      if (q <= 0) continue;
      extras += (Number(pricing[k]?.amount) || 0) * q;
    }
    if (addonsSelfServe && !addonsSelfIncluded && addonsSelfBoltKey && pricing[addonsSelfBoltKey]) {
      extras += Number(pricing[addonsSelfBoltKey]?.amount) || 0;
    }
    return Math.round((base + extras) * 100) / 100;
  })();

  const continueAddons = async () => {
    if (!pricing || !addonsFor) return;
    const items = [{ pass_type: addonsFor, qty: 1 }];
    for (const [k, qty] of Object.entries(addonsQty)) {
      const q = Number(qty) || 0;
      if (q > 0) items.push({ pass_type: k, qty: q });
    }
    if (addonsSelfServe && !addonsSelfIncluded && addonsSelfBoltKey) {
      items.push({ pass_type: addonsSelfBoltKey, qty: 1 });
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/festival-basket/checkout`, {
        items,
        festival_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  const closeAddons = () => { setAddonsFor(null); setAddonsQty({}); setAddonsSelfServe(false); };

  // Iter371 · Fix 4 — Load Purchase History on demand. Endpoint is
  // owner + deputy only; anything else 403s so we keep the button
  // hidden. We fetch lazily so the initial festival load isn't
  // slowed by a payments join.
  const loadPurchases = async () => {
    setPurchasesBusy(true);
    try {
      const { data } = await api.get(`/festival/${id}/purchases`);
      setPurchases(data.purchases || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load purchase history");
      setPurchases([]);
    } finally {
      setPurchasesBusy(false);
    }
  };
  const toggleAdvanced = () => {
    const next = !advancedOpen;
    setAdvancedOpen(next);
    if (next && purchases === null) loadPurchases();
  };

  const grantComplimentary = async () => {
    const reason = window.prompt("Reason for complimentary access?");
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post(`/festival/${id}/complimentary`, { reason: reason.trim() });
      toast.success("Complimentary access granted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const extendDemo = async () => {
    const minsStr = window.prompt("Extend demo by how many minutes?");
    const mins = Number(minsStr);
    if (!mins || mins < 1) return;
    const reason = window.prompt("Reason?");
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post(`/festival/${id}/demo/extend`, { extra_minutes: mins, reason: reason.trim() });
      toast.success(`Demo extended by ${mins} min`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!festival) return <div className="p-8 text-sm text-[#A3A3A3]">Loading…</div>;

  const st = STATE_LABELS[festival.license?.state] || STATE_LABELS.draft;
  const isOperational = festival.license?.is_operational;
  // Iter316 — Draft festivals that never activated may be deleted by
  // the owner (or platform admin). Mirrors the backend allow-list.
  const deletableStates = new Set(["draft", "demo_active", "demo_expired", "draft_expired"]);
  const canDeleteDraft = deletableStates.has(festival.license?.state);

  const doDelete = async () => {
    if (deleteConfirmText.trim().toUpperCase() !== "DELETE") {
      toast.error('Type DELETE to confirm');
      return;
    }
    // Iter316 — 5-second undo window. We navigate back to the list
    // immediately (so it feels responsive) and only fire the DELETE
    // after the timer elapses. Undo cancels the timer.
    const festivalName = festival.name;
    setShowDeleteModal(false);
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled) return;
      try {
        await api.delete(`/festival/${id}`);
        toast.success("Draft festival deleted");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Delete failed");
      }
    }, 5000);
    toast(`Deleting "${festivalName}"…`, {
      description: "Undo within 5 seconds",
      duration: 5000,
      action: {
        label: "Undo",
        onClick: () => {
          cancelled = true;
          clearTimeout(timer);
          toast.success("Delete cancelled");
          // Undo means we stay on the list — the row is still there
          // server-side because we never called DELETE.
        },
      },
    });
    navigate("/festival/mine");
  };

  const fromAdmin = isFromAdminFestivals();
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="festival-detail">
      <div className="flex items-center justify-between gap-3 mb-4">
        <button
          type="button"
          onClick={() => {
            if (fromAdmin) { clearAdminFestivalEntry(); navigate("/admin/festivals"); }
            else navigate("/festival/mine");
          }}
          data-testid={fromAdmin ? "back-to-festival-manager" : "back-all-festivals"}
          className={`text-[11px] uppercase tracking-wider inline-flex items-center gap-1 ${fromAdmin ? "text-[#CCFF00] hover:text-white" : "text-[#A3A3A3] hover:text-white"}`}
        >
          <ArrowLeft className="w-3.5 h-3.5" /> {fromAdmin ? "Back to Festival Manager" : "All festivals"}
        </button>
        {/* Iter456 — Toolkit tab. Opens the goodwill-override controls
             (admin-only writes) + audit log (both admin and the
             festival owner can read; organiser sees the admin name
             masked server-side). Rendered on every festival-detail
             view since this page is already owner-or-admin-gated by
             the backend; the Toolkit page itself gates writes. */}
        <button
          type="button"
          onClick={() => navigate(`/festival/${id}/toolkit`)}
          data-testid="festival-detail-toolkit-link"
          className="text-[11px] uppercase tracking-wider inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black"
          title={isPlatformOwner ? "Admin overrides + audit log (scoped to this festival)" : "View the override activity for this festival"}
        >
          <Wrench className="w-3.5 h-3.5" />
          {/* Iter457 · Fix 4 — Organiser view sees "Festival Activity
               Log" (same page, read-only audit view); admin / owner
               sees "Toolkit" since they can still write overrides. */}
          {isPlatformOwner ? "Toolkit" : "Festival Activity Log"}
        </button>
      </div>
      <OutstandingExtensions festivalId={festival.id} onPaid={load} />
      <EarlyAccessBanner festivalId={festival.id} startAt={festival.start_at} />

      {/* Iter316 — Draft-expired lock banner. Fires when the Start date
          has passed on an unactivated draft (or a paid license later
          expired). Whole page reads as read-only and the organiser is
          nudged to create a new festival.
          Iter463 · Fix 2 — Label logic now splits on the underlying
          license state. A festival that was previously active_paid /
          complimentary / override and whose activation window has
          since ended shows "EXPIRED"; a genuine never-paid draft that
          ran past its start date keeps "DRAFT EXPIRED". */}
      {festival.license?.is_locked && (
        <div
          className="border border-[#FF4500] bg-[#1A0808] p-4 mb-6 flex items-center justify-between gap-3 flex-wrap"
          data-testid="festival-draft-expired-banner"
        >
          <div className="flex items-start gap-3 min-w-0">
            <AlertTriangle className="w-5 h-5 text-[#FF4500] shrink-0 mt-0.5" />
            <div className="min-w-0">
              {festival.license?.state === "expired" ? (
                <>
                  <div className="label-mono text-[#FF4500] text-[10px]" data-testid="festival-expired-label">EXPIRED · READ-ONLY</div>
                  <div className="text-sm text-white mt-1">
                    This festival's activation period has ended.
                    Edits, pass purchase and player imports are locked.
                  </div>
                </>
              ) : (
                <>
                  <div className="label-mono text-[#FF4500] text-[10px]" data-testid="festival-draft-expired-label">DRAFT EXPIRED · READ-ONLY</div>
                  <div className="text-sm text-white mt-1">
                    This draft festival's Start date has passed without a paid activation.
                    Edits, pass purchase and player imports are locked.
                  </div>
                </>
              )}
            </div>
          </div>
          <Link
            to="/festival/new"
            data-testid="festival-create-new-cta"
            className="inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-wider font-bold shrink-0"
            style={{ background: "#CCFF00", color: "#0A0A0A" }}
          >
            <PartyPopper className="w-4 h-4" /> Create new festival
          </Link>
        </div>
      )}

      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div className="min-w-0">
          <div className="label-mono text-[#CCFF00] mb-1">
            RALLY-LAB · {festival.template === "friendly_mix" ? "FRIENDLY MIX" : "MATCH / SCORED"}
          </div>
          <h1 className="h-display text-3xl sm:text-4xl flex items-center gap-2">
            <PartyPopper className="w-7 h-7 text-[#CCFF00]" />
            {festival.name}
          </h1>
          {festival.venue_name && <div className="text-sm text-[#A3A3A3] mt-1">📍 {festival.venue_name}</div>}
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${st.tone}`} data-testid="festival-state-badge">
          {st.label}
        </span>
      </div>

      {/* Iter316 — Delete draft button. Only visible on pre-activation
          states so an active/paid festival can't be nuked from the UI. */}
      {canDeleteDraft && (
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            onClick={() => { setDeleteConfirmText(""); setShowDeleteModal(true); }}
            data-testid="festival-delete-draft-cta"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-white"
            title="Delete this draft festival"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete draft
          </button>
        </div>
      )}

      {showDeleteModal && (
        <div
          className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center p-4"
          onClick={() => !busy && setShowDeleteModal(false)}
          data-testid="festival-delete-modal"
        >
          <div
            className="w-full max-w-md bg-[#0A0A0A] border border-[#FF4500] p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 text-[#FF4500]">
              <AlertTriangle className="w-5 h-5" />
              <div className="label-mono text-xs">DELETE DRAFT FESTIVAL</div>
            </div>
            <div className="text-sm text-white">
              Permanently delete <span className="font-bold">{festival.name}</span> and its players, courts and helper passes?
              This can't be undone.
            </div>
            <div>
              <label className="label-mono text-[#A3A3A3] text-[10px] block mb-1">Type DELETE to confirm</label>
              <input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                autoFocus
                data-testid="festival-delete-confirm-input"
                className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF4500]"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={busy}
                data-testid="festival-delete-cancel"
                className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#525252] text-[#A3A3A3] hover:border-white hover:text-white disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={busy || deleteConfirmText.trim().toUpperCase() !== "DELETE"}
                data-testid="festival-delete-confirm"
                className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold bg-[#FF4500] text-white hover:bg-[#FF6633] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete forever
              </button>
            </div>
          </div>
        </div>
      )}

      {/* License / activation card */}
      <div className="card-surface p-6 mb-6" data-testid="festival-license-card">
        <div className="flex items-center gap-2 mb-3">
          {isOperational ? <CheckCircle2 className="w-5 h-5 text-[#CCFF00]" /> : <AlertTriangle className="w-5 h-5 text-[#FFB020]" />}
          <div className="h-display text-lg">
            {isOperational ? "Live ops unlocked" : "Live ops locked"}
          </div>
        </div>

        {festival.license?.state === "demo_active" && (
          <div className="text-sm text-[#E5E5E5] mb-4">
            Demo ends in <span className="text-[#CCFF00] font-mono text-lg" data-testid="festival-demo-countdown">{formatSeconds(remaining)}</span>
            {remaining === 0 && <span className="ml-2 text-[#FFB020]">— refresh to lock live ops</span>}
          </div>
        )}
        {festival.license?.state === "demo_expired" && (
          <p className="text-sm text-[#A3A3A3] mb-4">
            Demo period is done. Pick a pass below to unlock the festival for the event window.
          </p>
        )}
        {festival.license?.state === "draft" && (
          <p className="text-sm text-[#A3A3A3] mb-4">
            Start a 5-minute demo to try court assignment, timers, public viewer and self-service without paying anything.
          </p>
        )}
        {(festival.license?.state === "active_paid" || festival.license?.state === "complimentary" || festival.license?.state === "override") && (
          <div className="text-sm text-[#A3A3A3] mb-4">
            {/* Iter316 B7 — Full-detail Live Ops Unlocked bar. Tier name
                prominent, duration underneath, add-on tags on the right,
                expiry line below. */}
            {(() => {
              const passType = festival.license?.pass_type || "";
              const tierRaw = passType ? passType.split("_")[0] : (festival.license?.state === "complimentary" ? "complimentary" : "override");
              const tierLabel = tierRaw.toUpperCase();
              // Iter339 · Bug 17 — Prefer the backend-computed
              // planned_days (midnight-crossing aware) over parsing
              // the days from the pass_type string. Legacy passes on
              // a 2-day midnight-crossing event may still be tagged
              // `_1day` even though the event runs 2 days.
              const daysMatch = passType.match(/_(\d+)day/);
              const days = (Number(festival?.planned_days) || null)
                || (daysMatch ? parseInt(daysMatch[1], 10) : null);
              const addons = festival.license?.addons_summary || [];
              return (
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                  <div>
                    <div
                      className="inline-block text-xs uppercase tracking-widest px-2 py-1 border border-[#CCFF00] text-[#CCFF00] font-bold"
                      data-testid="festival-tier-badge"
                      title={passType || tierRaw}
                    >
                      {tierLabel}
                    </div>
                    {days ? (
                      <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-1" data-testid="festival-tier-duration">
                        {days}-DAY PASS
                      </div>
                    ) : null}
                  </div>
                  {addons.length > 0 && (
                    <div className="flex flex-wrap gap-1 max-w-[65%] justify-end" data-testid="festival-addons-tags">
                      {addons.map((a, i) => (
                        <span
                          key={i}
                          className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-[#66C2FF] text-[#66C2FF]"
                        >
                          {a}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            Access active until <span className="text-white">{new Date(festival.license.activation_ends_at).toLocaleString()}</span>
            {festival.license.complimentary_reason && <span className="ml-1">· {festival.license.complimentary_reason}</span>}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {festival.license?.state === "draft" && (
            <button
              type="button"
              onClick={startDemo}
              disabled={busy}
              data-testid="festival-start-demo"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-bold disabled:opacity-50"
              style={{ background: "#CCFF00", color: "#0A0A0A" }}
            >
              <Zap className="w-4 h-4" /> Start 5-min demo
            </button>
          )}
          {isPlatformOwner && festival.license?.state === "demo_active" && (
            <button
              type="button"
              onClick={extendDemo}
              disabled={busy}
              data-testid="festival-extend-demo"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black"
            >
              <Wrench className="w-4 h-4" /> Extend demo
            </button>
          )}
          {isPlatformOwner && festival.license?.state !== "complimentary" && (
            <button
              type="button"
              onClick={grantComplimentary}
              disabled={busy}
              data-testid="festival-grant-complimentary"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black"
            >
              <Gift className="w-4 h-4" /> Grant complimentary
            </button>
          )}
        </div>
      </div>

      {/* Iter325 — Request a custom package (visible pre-checkout to
          all viewers; the owner sees it too so support can proactively
          request context on someone else's festival). */}
      {festival.license?.state !== "draft" ? null : !customPkgOpen ? (
        <button
          type="button"
          onClick={() => setCustomPkgOpen(true)}
          data-testid="festival-custom-pkg-open"
          className="mb-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#66C2FF] hover:text-white"
        >
          <MessageSquare className="w-3.5 h-3.5" /> Request a custom package
        </button>
      ) : (
        <div className="mb-4 border border-[#66C2FF] p-3" data-testid="festival-custom-pkg-form">
          <div className="label-mono text-[#66C2FF] text-[10px] mb-2">CUSTOM PACKAGE REQUEST</div>
          <textarea
            value={customPkgText}
            onChange={(e) => setCustomPkgText(e.target.value)}
            placeholder="Tell us what you need — extra player capacity, sponsor packages, bespoke helper counts, longer event window…"
            maxLength={2000}
            data-testid="festival-custom-pkg-textarea"
            className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white text-sm focus:border-[#66C2FF] focus:outline-none min-h-[80px]"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setCustomPkgOpen(false); setCustomPkgText(""); }}
              disabled={customPkgBusy}
              className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#A3A3A3] hover:text-white disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={customPkgBusy || !customPkgText.trim()}
              onClick={async () => {
                setCustomPkgBusy(true);
                try {
                  await api.post(`/festival/enquiries`, {
                    name: user?.name || "Organiser",
                    email: user?.email || "unknown@rally-lab.com",
                    message: `[${festival?.name}] ${customPkgText.trim()}`,
                    items: [festival?.id || ""],
                  });
                  toast.success("Request sent — Rally-Lab will be in touch shortly.");
                  setCustomPkgOpen(false);
                  setCustomPkgText("");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Couldn't send request");
                } finally { setCustomPkgBusy(false); }
              }}
              data-testid="festival-custom-pkg-submit"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold bg-[#66C2FF] text-black hover:bg-white disabled:opacity-40"
            >
              {customPkgBusy ? "Sending…" : "Send request"}
            </button>
          </div>
        </div>
      )}

      {/* Passes */}
      {!isPlatformOwner && !festival.license?.is_locked && (festival.license?.state !== "active_paid" && festival.license?.state !== "complimentary" && festival.license?.state !== "override") && (
        <div className="card-surface p-6 mb-6" data-testid="festival-passes-card">
          <div className="label-mono text-[#CCFF00] mb-2">ACTIVATION PASSES</div>
          <h2 className="h-display text-xl mb-2">Unlock the full event</h2>

          {/* Iter316 B6 — Events longer than 3 days can't buy any of the
              standard passes. Show a bespoke-CTA banner instead of the
              greyed grid so the organiser has a clear next step. */}
          {(Number(festival?.planned_days) || 0) > 3 ? (
            <div
              className="border border-[#66C2FF] bg-[#0A1A2A] p-4 flex items-start gap-3"
              data-testid="festival-bespoke-cta"
            >
              <AlertTriangle className="w-5 h-5 text-[#66C2FF] shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="label-mono text-[#66C2FF] text-[10px]">BESPOKE PACKAGE REQUIRED</div>
                <div className="text-sm text-white mt-1">
                  Your event is {festival.planned_days} days long — our standard passes cap at 3 days.
                  Contact Rally-Lab and we'll build a bespoke package for the full run.
                </div>
                <a
                  href={`mailto:hello@rally-lab.com?subject=Rally-Lab%20bespoke%20package%20for%20${encodeURIComponent(festival.name || "festival")}%20(${festival.planned_days}%20days)`}
                  className="inline-block mt-2 text-[10px] uppercase tracking-widest text-[#CCFF00] hover:text-white"
                >
                  Contact us -&gt;
                </a>
              </div>
            </div>
          ) : (() => {
            // Iter471 — Regression: for a 2-day festival the tier grid
            // was rendering all 12 pass-type tiles (starter/standard/pro/
            // unlimited × 1/2/3-day) with mismatched day-counts greyed
            // out. Spec requires the grid to show the four tiers with
            // their matching day-count only. Hoist the event-day
            // calculation and filter at the entry level so exactly four
            // tiles render — one per tier at the correct duration.
            const eventDays = Number(festival?.planned_days) || 1;
            return (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {/* Iter303 — only surface the four tier passes (not bolt-ons)
                in this checkout grid; POA rows render as Contact tiles
                and never hit the Stripe endpoint. */}
            {pricing && Object.entries(pricing)
              .filter(([k, def]) => !k.startsWith("bolton_") && def?.tier !== "bolton")
              .filter(([, def]) => {
                // Iter471 — Only show tiers whose day-count matches the
                // selected festival duration. Pass-types with no `days`
                // field (bespoke / custom tiers without a duration) still
                // pass through so admin-added bespoke rows are visible.
                const passDays = Number(def?.days) || 0;
                return passDays === 0 || passDays === eventDays;
              })
              .map(([key, def]) => {
                const isPoa = def.poa || !(Number(def.amount) > 0);
                // Iter316 — Grey out passes whose duration doesn't match
                // the calculated event duration. A 1-day event may only
                // buy 1-day passes; a 3-day event may only buy 3-day
                // passes. Mismatched tiles show a "not valid for a N-day
                // event" tooltip and are not clickable.
                // Iter471 — Upstream filter now removes mismatches
                // entirely so this check is a safety net for bespoke
                // tiers with no `days` set.
                const passDays = Number(def?.days) || 0;
                const durationMismatch = passDays > 0 && passDays !== eventDays;
                // Iter327 · Bug 5 — Cap-vs-roster gate. Grey out any
                // tier whose player_cap is smaller than the current
                // imported roster so the organiser can't buy a pass
                // that won't run their event. Unlimited (`null`) tiers
                // and unknown counts are always allowed.
                const cap = def?.player_cap;
                const capMismatch = capCount != null && cap != null && cap < capCount;
                const mismatchLabel = durationMismatch
                  ? `Not valid for a ${eventDays}-day event · this pass covers ${passDays} day${passDays === 1 ? "" : "s"}`
                  : (capMismatch
                    ? `Roster is ${capCount} players — this tier caps at ${cap}. Pick a bigger pack.`
                    : "");
                const disabled = durationMismatch || capMismatch;
                if (isPoa) {
                  return (
                    <a
                      key={key}
                      href={disabled ? undefined : "mailto:hello@rally-lab.com?subject=Rally-Lab%20Festival%20tier%20enquiry"}
                      data-testid={`festival-poa-${key}`}
                      title={mismatchLabel || undefined}
                      onClick={(e) => { if (disabled) e.preventDefault(); }}
                      className={`text-left border p-4 transition-colors ${disabled ? "border-[#141414] opacity-40 cursor-not-allowed pointer-events-none" : "border-[#525252] hover:border-white"}`}
                    >
                      <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">{def.label || key}</div>
                      <div className="text-lg font-bold text-white mt-1">POA</div>
                      <div className="text-[10px] uppercase tracking-wider text-[#525252] mt-1">
                        Up to {def.player_cap === null || def.player_cap === undefined ? "unlimited" : def.player_cap} players
                      </div>
                      {disabled ? (
                        <div className="mt-3 text-[10px] uppercase tracking-wider text-[#FFB020]" data-testid={`festival-tier-mismatch-${key}`}>
                          {mismatchLabel}
                        </div>
                      ) : (
                        <div className="mt-3 text-[10px] uppercase tracking-wider text-white inline-flex items-center gap-1">
                          Contact us →
                        </div>
                      )}
                    </a>
                  );
                }
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => startCheckout(key)}
                    disabled={busy || disabled}
                    data-testid={`festival-buy-${key}`}
                    data-cap-blocked={capMismatch ? "1" : "0"}
                    title={mismatchLabel || undefined}
                    className={`text-left border p-4 transition-colors disabled:cursor-not-allowed ${disabled ? "border-[#141414] opacity-40" : "border-[#262626] hover:border-[#CCFF00] disabled:opacity-50"}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">{def.label || key}</div>
                    <div className={`text-2xl font-bold mt-1 ${disabled ? "text-[#525252]" : "text-[#CCFF00]"}`}>
                      {formatMoney(def.amount, def.currency)}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-[#525252] mt-1">
                      {def.days} day{def.days > 1 ? "s" : ""} · up to {def.player_cap === null || def.player_cap === undefined ? "∞" : def.player_cap} players
                    </div>
                    {disabled ? (
                      <div className="mt-3 text-[10px] uppercase tracking-wider text-[#FFB020]" data-testid={`festival-tier-mismatch-${key}`}>
                        {mismatchLabel}
                      </div>
                    ) : (
                      <div className="mt-3 text-[10px] uppercase tracking-wider text-[#CCFF00] inline-flex items-center gap-1">
                        <ShoppingCart className="w-3 h-3" /> Pay & unlock
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            );
          })()}
        </div>
      )}

      {/* Iter316 B1 — Add-ons checkout modal. Renders after the organiser
          clicks a paid tier tile and before we redirect to Stripe. Lets
          them tick Self-Service (if their tier doesn't include it) and
          add extra helper bundles with a live order summary. Continue
          posts to /festival-basket/checkout so the license activates
          with helper_seats_bought[...] set. */}
      {addonsFor && pricing?.[addonsFor] && (
        <div
          className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center p-4"
          onClick={() => !busy && closeAddons()}
          data-testid="festival-addons-modal"
        >
          <div
            className="w-full max-w-lg bg-[#0A0A0A] border-2 border-[#CCFF00] p-5 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="label-mono text-[#CCFF00] text-[10px]">STEP 2 · ADD-ONS</div>
                <h2 className="h-display text-xl mt-1">Configure your pass</h2>
                <p className="text-xs text-[#A3A3A3] mt-1">
                  {pricing[addonsFor].label || addonsFor} · base {formatMoney(pricing[addonsFor].amount, pricing[addonsFor].currency)}
                </p>
                {/* Iter325 — Side-by-side price when a bespoke package
                    exists. Shows the standard amount, the organiser's
                    bespoke amount and the saving in one glance. */}
                {bespoke && bespoke.tier_amount != null && (
                  <div
                    className="mt-2 border border-[#66C2FF] bg-[#0D1A22] p-2"
                    data-testid="festival-addons-bespoke-pricing"
                  >
                    <div className="label-mono text-[#66C2FF] text-[10px]">
                      BESPOKE · {bespoke.name}
                    </div>
                    <div className="mt-1 flex items-center gap-3 flex-wrap text-xs">
                      <span className="text-[#525252] line-through" data-testid="festival-addons-standard-price">
                        {formatMoney(pricing[addonsFor].amount, pricing[addonsFor].currency)}
                      </span>
                      <span className="text-[#CCFF00] font-bold text-sm" data-testid="festival-addons-bespoke-price">
                        {formatMoney(bespoke.tier_amount, pricing[addonsFor].currency)}
                      </span>
                      {bespoke.tier_amount < Number(pricing[addonsFor].amount) && (
                        <span className="text-[10px] uppercase tracking-widest text-[#FFB020]" data-testid="festival-addons-saving">
                          Save {formatMoney(Number(pricing[addonsFor].amount) - Number(bespoke.tier_amount), pricing[addonsFor].currency)}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={closeAddons}
                data-testid="festival-addons-close"
                className="text-[#A3A3A3] hover:text-white"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {/* Self-Service — toggle only if the tier doesn't include it. */}
            <div className="border border-[#262626] p-3 mb-3" data-testid="festival-addons-self-service">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="label-mono text-[10px] text-[#66C2FF]">PLAYER SELF-SERVICE</div>
                  <div className="text-sm text-white mt-0.5">Let players check themselves in + queue via their phone</div>
                  <div className="text-[10px] text-[#525252] mt-1">
                    {addonsSelfIncluded
                      ? "Included in this tier."
                      : addonsSelfBoltKey && pricing[addonsSelfBoltKey]
                        ? `Bolt-on: +${formatMoney(pricing[addonsSelfBoltKey].amount, pricing[addonsSelfBoltKey].currency)}`
                        : "Contact us for a bespoke self-service package."}
                  </div>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={addonsSelfServe}
                    disabled={addonsSelfIncluded || !addonsSelfBoltKey}
                    onChange={(e) => setAddonsSelfServe(e.target.checked)}
                    data-testid="festival-addons-self-service-toggle"
                    className="accent-[#CCFF00] w-4 h-4 disabled:opacity-40"
                  />
                  <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                    {addonsSelfIncluded ? "Included" : addonsSelfServe ? "Added" : "Off"}
                  </span>
                </label>
              </div>
            </div>

            {/* Helper bundles */}
            <div className="border border-[#262626] p-3 mb-3">
              <div className="label-mono text-[10px] text-[#66C2FF] mb-2">HELPER BUNDLES</div>
              <div className="space-y-2">
                {HELPER_BOLTONS.map((row) => {
                  const def = pricing[row.key];
                  if (!def) return null;
                  const canBuy = !def.poa && Number(def.amount) > 0;
                  const qty = Number(addonsQty[row.key] || 0);
                  return (
                    <div key={row.key} className="flex items-start justify-between gap-3" data-testid={`festival-addons-${row.key}`}>
                      <div className="min-w-0">
                        <div className="text-sm text-white">{row.label}</div>
                        <div className="text-[10px] text-[#525252] mt-0.5">{row.hint}</div>
                        <div className="text-[10px] text-[#A3A3A3] mt-0.5">
                          {canBuy ? `${formatMoney(def.amount, def.currency)} each` : "POA — contact us"}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => setAddonsQty((q) => ({ ...q, [row.key]: Math.max(0, (Number(q[row.key]) || 0) - 1) }))}
                          disabled={!canBuy || qty <= 0}
                          data-testid={`festival-addons-${row.key}-minus`}
                          className="w-7 h-7 border border-[#262626] text-white hover:border-[#CCFF00] hover:text-[#CCFF00] disabled:opacity-30 disabled:cursor-not-allowed"
                          aria-label="Decrease"
                        >
                          −
                        </button>
                        <span data-testid={`festival-addons-${row.key}-qty`} className="w-8 text-center text-sm text-white">
                          {qty}
                        </span>
                        <button
                          type="button"
                          onClick={() => setAddonsQty((q) => ({ ...q, [row.key]: (Number(q[row.key]) || 0) + 1 }))}
                          disabled={!canBuy}
                          data-testid={`festival-addons-${row.key}-plus`}
                          className="w-7 h-7 border border-[#262626] text-white hover:border-[#CCFF00] hover:text-[#CCFF00] disabled:opacity-30 disabled:cursor-not-allowed"
                          aria-label="Increase"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Order summary */}
            <div className="border border-[#525252] bg-[#0F0F0F] p-3 mb-3" data-testid="festival-addons-summary">
              <div className="label-mono text-[10px] text-[#A3A3A3] mb-2">ORDER SUMMARY</div>
              <div className="text-xs text-white flex justify-between mb-1">
                <span>{pricing[addonsFor].label || addonsFor}</span>
                <span>{formatMoney(pricing[addonsFor].amount, pricing[addonsFor].currency)}</span>
              </div>
              {addonsSelfServe && !addonsSelfIncluded && addonsSelfBoltKey && pricing[addonsSelfBoltKey] && (
                <div className="text-xs text-white flex justify-between mb-1" data-testid="festival-addons-summary-self">
                  <span>Player Self-Service</span>
                  <span>+{formatMoney(pricing[addonsSelfBoltKey].amount, pricing[addonsSelfBoltKey].currency)}</span>
                </div>
              )}
              {HELPER_BOLTONS.map((row) => {
                const qty = Number(addonsQty[row.key] || 0);
                if (qty <= 0) return null;
                const def = pricing[row.key];
                if (!def) return null;
                return (
                  <div key={row.key} className="text-xs text-white flex justify-between mb-1" data-testid={`festival-addons-summary-${row.key}`}>
                    <span>{row.label} × {qty}</span>
                    <span>+{formatMoney((Number(def.amount) || 0) * qty, def.currency)}</span>
                  </div>
                );
              })}
              <div className="text-sm font-bold text-[#CCFF00] flex justify-between border-t border-[#262626] pt-2 mt-2" data-testid="festival-addons-total">
                <span>Total</span>
                <span>{formatMoney(addonsTotal, pricing[addonsFor].currency)}</span>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeAddons}
                disabled={busy}
                data-testid="festival-addons-back"
                className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#525252] text-[#A3A3A3] hover:border-white hover:text-white disabled:opacity-50"
              >
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
              <button
                type="button"
                onClick={continueAddons}
                disabled={busy}
                data-testid="festival-addons-continue"
                className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold bg-[#CCFF00] text-black hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ShoppingCart className="w-4 h-4" /> {busy ? "Redirecting…" : "Continue to payment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event window */}
      <EventWindowCard festival={festival} onSaved={load} />

      {/* Iter180 — Player Manager entry point (Phase 2). Shows for any
          state so coaches can prep the roster during the demo period. */}
      <Link
        to={`/festival/${festival.id}/players`}
        data-testid="festival-players-link"
        className="card-surface p-6 flex items-center justify-between hover:border-[#CCFF00] transition-colors mb-4"
      >
        <div>
          <div className="label-mono text-[#CCFF00] mb-1">PLAYER MANAGER · PHASE 2</div>
          <div className="h-display text-lg">Roster, import & rating overrides</div>
          <p className="text-xs text-[#A3A3A3] mt-1">
            CSV import (Booking ID · Name · SAR), manual walk-ins, colour bands, sin-bin flow.
          </p>
        </div>
        <span className="text-[#CCFF00] text-2xl">→</span>
      </Link>

      {/* Iter181 — Live Ops entry point (Phase 3a). Only surfaced once the
          festival is operational (demo / active / complimentary / override). */}
      {festival.license?.is_operational && (
        <Link
          to={`/festival/${festival.id}/live`}
          data-testid="festival-live-ops-link"
          className="card-surface p-6 flex items-center justify-between hover:border-[#CCFF00] transition-colors"
          style={{ borderColor: "#CCFF00" }}
        >
          <div>
            <div className="label-mono text-[#CCFF00] mb-1">LIVE OPS · PHASE 3a</div>
            <div className="h-display text-lg">Run the festival</div>
            <p className="text-xs text-[#A3A3A3] mt-1">
              Court timers, group-of-4 assignment, quick actions and the public viewer.
            </p>
          </div>
          <span className="text-[#CCFF00] text-2xl">▶</span>
        </Link>
      )}

      {/* Iter187 — QR Kiosk (print sheet) + Analytics dashboard. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {festival.license?.self_service_enabled !== false && (
          <Link
            to={`/festival/${festival.id}/kiosk`}
            data-testid="festival-kiosk-link"
            className="card-surface p-6 flex items-center justify-between hover:border-[#CCFF00] transition-colors"
          >
            <div>
              <div className="label-mono text-[#CCFF00] mb-1">QR KIOSK</div>
              <div className="h-display text-lg">Print self-service codes</div>
              <p className="text-xs text-[#A3A3A3] mt-1">
                One QR per player · pin at the check-in desk.
              </p>
            </div>
            <span className="text-[#CCFF00] text-2xl">🖨</span>
          </Link>
        )}
        <Link
          to={`/festival/${festival.id}/helpers`}
          data-testid="festival-helpers-link"
          className="card-surface p-6 flex items-center justify-between hover:border-[#CCFF00] transition-colors"
        >
          <div>
            <div className="label-mono text-[#CCFF00] mb-1">HELPER PASSES</div>
            <div className="h-display text-lg">Shift passes for volunteers</div>
            <p className="text-xs text-[#A3A3A3] mt-1">
              QR + code · scoped roles · auto-expire when the shift ends.
            </p>
          </div>
          <span className="text-[#CCFF00] text-2xl">🎫</span>
        </Link>
        <Link
          to={`/festival/${festival.id}/analytics`}
          data-testid="festival-analytics-link"
          className="card-surface p-6 flex items-center justify-between hover:border-[#CCFF00] transition-colors"
        >
          <div>
            <div className="label-mono text-[#CCFF00] mb-1">ANALYTICS</div>
            <div className="h-display text-lg">Post-event snapshot</div>
            <p className="text-xs text-[#A3A3A3] mt-1">
              Games played, avg game length, band mix, no-shows.
            </p>
          </div>
          <span className="text-[#CCFF00] text-2xl">📈</span>
        </Link>
      </div>

      {/* Iter373 · Fix 2 — Festival Management: Rally-Lab platform
           owner only. Currently exposes the festival-wide device
           limit for helper passes (1-6, default 1). Rendered right
           above the collapsible Advanced section. */}
      {(isPlatformOwner || festival.tenant_id === user?.id) && festival.license?.state && festival.license.state !== "draft" && (
        <PostFestivalEmailsCard festival={festival} onChange={load} />
      )}

      {isPlatformOwner && (
        <div className="mt-6 border border-[#B26BFF] bg-[#180C24] p-4" data-testid="festival-management-section">
          <div className="label-mono text-[10px] text-[#B26BFF] mb-2 inline-flex items-center gap-1">
            <Settings2 className="w-3 h-3" /> FESTIVAL MANAGEMENT · OWNER ONLY
          </div>
          <div className="text-sm text-white">Default device limit per helper pass, by role</div>
          <div className="text-[10px] text-[#A3A3A3] mt-0.5 mb-3">
            Default 1 per role — a second device signing in kicks the first out. Range 1–6. Individual passes can be overridden on the Helpers page.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {DEVICE_LIMIT_ROLES.map(([role, label]) => (
              <label key={role} className="flex items-center justify-between gap-3 border border-[#2A1A3A] px-3 py-2">
                <span className="text-sm text-white">{label}</span>
                <select
                  value={Number(festival?.device_limits_by_role?.[role]) || 1}
                  onChange={async (e) => {
                    const v = Number(e.target.value);
                    try {
                      await api.put(`/festival/${id}/device-limit`, { role, device_limit: v });
                      toast.success(`${label}: device limit set to ${v}`);
                      await load();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Failed to update device limit");
                    }
                  }}
                  data-testid={`festival-device-limit-${role}`}
                  className="bg-[#0A0A0A] border border-[#B26BFF] px-3 py-2 text-sm text-white focus:outline-none focus:border-white"
                >
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>{n} device{n === 1 ? "" : "s"}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Iter371 · Fix 4 — Advanced settings section with the full
           Purchase History log for Organiser + Deputy. Read-only;
           the backend endpoint returns 403 for any other role so
           we render the toggle for everyone and let the click
           reveal the empty/gated state. Collapsed by default. */}
      <div className="mt-6" data-testid="festival-advanced-section">
        <button
          type="button"
          onClick={toggleAdvanced}
          data-testid="festival-advanced-toggle"
          className="w-full flex items-center justify-between gap-2 border border-[#262626] bg-[#0A0A0A] hover:border-[#66C2FF] p-3 text-left transition-colors"
        >
          <span className="inline-flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-[#66C2FF]" />
            <span className="label-mono text-[10px] text-[#66C2FF]">ADVANCED</span>
            <span className="text-sm text-white">Purchase history & billing log</span>
          </span>
          {advancedOpen
            ? <ChevronUp className="w-4 h-4 text-[#A3A3A3]" />
            : <ChevronDown className="w-4 h-4 text-[#A3A3A3]" />}
        </button>
        {advancedOpen && (
          <div className="border border-t-0 border-[#262626] bg-[#0A0A0A] p-4" data-testid="festival-advanced-body">
            <div className="label-mono text-[10px] text-[#66C2FF] mb-2 inline-flex items-center gap-1">
              <Receipt className="w-3 h-3" /> PURCHASE HISTORY
            </div>
            <p className="text-[11px] text-[#A3A3A3] mb-3">
              Every transaction against this festival, in reverse chronological order. Organiser + Deputy only.
            </p>
            {purchasesBusy && (
              <div className="text-xs text-[#A3A3A3]" data-testid="festival-purchases-loading">Loading…</div>
            )}
            {!purchasesBusy && Array.isArray(purchases) && purchases.length === 0 && (
              <div className="text-xs text-[#525252] italic p-3 border border-[#141414]" data-testid="festival-purchases-empty">
                No purchases logged for this festival yet.
              </div>
            )}
            {!purchasesBusy && Array.isArray(purchases) && purchases.length > 0 && (
              <div className="border border-[#262626]" data-testid="festival-purchases-list">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-[#525252] border-b border-[#262626]">
                      <th className="text-left p-2">Item</th>
                      <th className="text-left p-2">Date</th>
                      <th className="text-right p-2">Amount</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map((tx, i) => {
                      const items = tx.items || [];
                      const isPaid = (tx.status || "").toLowerCase() === "paid";
                      const statusColor = isPaid ? "text-[#CCFF00]" : (tx.status === "pending" || tx.status === "initiated") ? "text-[#FFB020]" : "text-[#A3A3A3]";
                      return (
                        <tr key={tx.session_id || i} data-testid={`festival-purchase-row-${i}`} className="border-b border-[#141414] align-top">
                          <td className="p-2 text-white">
                            {items.length === 0 && <span className="text-[#A3A3A3]">{tx.purpose || "Purchase"}</span>}
                            {items.map((it, ii) => (
                              <div key={ii} className="flex items-center justify-between gap-3">
                                <span>{it.label}{it.qty > 1 ? ` × ${it.qty}` : ""}</span>
                                {items.length > 1 && (
                                  <span className="text-[10px] text-[#A3A3A3]">{formatMoney(it.amount, tx.currency)}</span>
                                )}
                              </div>
                            ))}
                          </td>
                          <td className="p-2 text-[#A3A3A3] whitespace-nowrap">
                            {tx.purchased_at ? new Date(tx.purchased_at).toLocaleString() : "—"}
                          </td>
                          <td className="p-2 text-right text-white whitespace-nowrap" data-testid={`festival-purchase-amount-${i}`}>
                            {formatMoney(tx.amount, tx.currency)}
                          </td>
                          <td className={`p-2 text-[10px] uppercase tracking-widest ${statusColor}`} data-testid={`festival-purchase-status-${i}`}>
                            {tx.status || "—"}
                          </td>
                          <td className="p-2 text-[10px] text-[#525252] break-all" title={tx.session_id || ""}>
                            {tx.session_id ? tx.session_id.slice(-12) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function formatMoney(amount, currency) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: (currency || "gbp").toUpperCase() }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}
